/**
 * `BACKUP CAREER`, on the store side.
 *
 * The engine decides what a backup *is* (`backupCareer` — a duplicate pilot
 * carrying the same seed under a new name); this decides whether one can be
 * written, which is a question about the roster and therefore about the store.
 * Both refusals are the manual's own:
 *
 * - *"If the career roster is full, you will be prompted to delete another
 *   pilot or to cancel."* — `saveCareer` already throws
 *   `THE CAREER ROSTER IS FULL!` for a new pilot past `MAX_PILOTS`, so the cap
 *   is enforced in exactly one place for enlistment and for backups alike.
 * - Backing a career up under its own pilot's name is refused here rather than
 *   allowed to succeed quietly. `careerId` is the name-slug plus the seed and a
 *   backup keeps the seed, so that case computes the *live* career's own id and
 *   `saveCareer` would replace the pilot with himself — a write that reports
 *   success and leaves nothing to restore from, which is the one outcome this
 *   feature exists to prevent.
 *
 * Returned as a result rather than thrown, because two of the three outcomes
 * are things the player is meant to read and act on.
 */

import { backupCareer, type CareerState } from "@red-opus/engine";
import { saveCareer, ROSTER_FULL_MESSAGE, type CareerSave, type CareerStore } from "./career-store.ts";

export type BackupOutcome =
  | { readonly kind: "saved"; readonly career: CareerState }
  | { readonly kind: "rosterFull" }
  | { readonly kind: "sameName" }
  | { readonly kind: "noName" };

/**
 * Writes a backup of `save` under `name`. The copy inherits the sortie the
 * original is sitting on (`lastConsequence`/`lastDebrief`) so that restoring it
 * puts the player back exactly where he was, mid-flow, rather than at an
 * aerodrome with the last mission's outcome silently dropped.
 */
export function writeBackup(store: CareerStore, save: CareerSave, name: string): BackupOutcome {
  const trimmed = name.trim();
  if (trimmed === "") return { kind: "noName" };
  const copy = backupCareer(save.career, trimmed);
  if (copy.id === save.career.id) return { kind: "sameName" };
  try {
    saveCareer(store, copy, { consequence: save.lastConsequence, debrief: save.lastDebrief });
  } catch (error) {
    if (error instanceof Error && error.message === ROSTER_FULL_MESSAGE) return { kind: "rosterFull" };
    throw error;
  }
  return { kind: "saved", career: copy };
}
